import React, { useState, useEffect, useMemo } from 'react';
import styles from './ProgressBar.module.css';
import { ProgressSegmentData } from './ProgressSegmentData';

type ProgressBarProps = {
    percentage?: number;
    segments?: ProgressSegmentData[];
    animate?: boolean;
};

const ProgressBar = ({ percentage = 0, segments, animate = true }: ProgressBarProps) => {
    const segmentWidths = useMemo(() => {
        if (!segments || segments.length === 0) {
            return [];
        }

        const total = segments.reduce((sum, seg) => sum + seg.value, 0);
        return total > 0 ? segments.map(seg => (seg.value / total) * 100) : segments.map(() => 0);
    }, [segments]);

    const targetPercentage = useMemo(() => Math.max(0, Math.min(percentage, 100)), [percentage]);

    const [animatedSegmentWidths, setAnimatedSegmentWidths] = useState<number[]>(() => (animate ? [] : segmentWidths));
    const [animatedFillWidth, setAnimatedFillWidth] = useState<number>(() => (animate ? 0 : targetPercentage));

    useEffect(() => {
        if (!segments || segments.length === 0) {
            setAnimatedSegmentWidths([]);
            return;
        }

        if (!animate) {
            setAnimatedSegmentWidths(segmentWidths);
            return;
        }

        setAnimatedSegmentWidths(segments.map(() => 0));

        const timer = window.setTimeout(() => {
            setAnimatedSegmentWidths(segmentWidths);
        }, 200);

        return () => window.clearTimeout(timer);
    }, [segments, segmentWidths, animate]);

    useEffect(() => {
        if (segments && segments.length > 0) {
            return;
        }

        if (!animate) {
            setAnimatedFillWidth(targetPercentage);
            return;
        }

        setAnimatedFillWidth(0);

        const timer = window.setTimeout(() => {
            setAnimatedFillWidth(targetPercentage);
        }, 150);

        return () => window.clearTimeout(timer);
    }, [segments, targetPercentage, animate]);

    if (segments && segments.length > 0) {
        return (
            <div className={styles.progressBarWrapper}>
                <div className={styles.progressBarContainer}>
                    <div className={styles.progressBarSegments}>
                        {segments.map((segment, index) => {
                            if (segment.value <= 0) {
                                return null;
                            }

                            const width = animate ? (animatedSegmentWidths[index] ?? 0) : (segmentWidths[index] ?? 0);

                            return (
                                <div
                                    key={index}
                                    className={styles.progressSegment}
                                    style={{
                                        width: `${width}%`,
                                        backgroundColor: segment.colour,
                                        transitionDelay: animate ? `${index * 120}ms` : undefined
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    const boundedFillWidth = Math.max(0, Math.min(100, animate ? animatedFillWidth : targetPercentage));

    return (
        <div className={styles.progressBarContainer}>
            <div
                className={`${styles.progressBarFill} ${boundedFillWidth > 75 ? styles.green : boundedFillWidth > 50 ? styles.yellow : ''}`}
                style={{ width: `${boundedFillWidth}%` }}
            ></div>
        </div>
    );
};

export default ProgressBar;
